// Owner Panel theme in the admin panel, at runtime. siteTheme.js stays the
// build-time default (vite.config.js reads it in Node, so it must not import
// browser code); this file lays the super owner's theme over it.
// Plan: docs/plans/2026-10-28-owner-panel-theme-media.md
import { useSyncExternalStore } from 'react';
import { SITE } from './siteTheme';
import { API_BASE_URL } from './config/api';
import api from './services/api';

// Same key as the player site: on a shared origin (tenexchn.com/admin) both
// read one copy; index.html paints it before first render.
const CACHE_KEY = 'ten:theme';

// What follows the theme beyond the header (which already uses --bg-primary):
// buttons, the sidebar's text and links. Added only once the owner has set
// colours, so an untouched site keeps today's admin exactly.
const OVERRIDES = `
.btn-primary, .btn-primary:hover, .btn-primary:focus, .btn-primary:active, .btn-primary:disabled {
  background-color: var(--bg-primary) !important; border-color: var(--bg-primary) !important; color: var(--text-primary) !important;
}
body[data-sidebar="dark"] .point-inner #sidebar-menu ul li a,
body[data-sidebar="dark"] .point-inner #sidebar-menu ul li ul.sub-menu li a,
body[data-sidebar="dark"] .point-inner #sidebar-menu ul li a i { color: var(--text-body) !important; }
.main-content a:not(.btn):not(.nav-link):not(.dropdown-item):not(.page-link) { color: var(--bg-secondary); }
`;

let brand = { logo: SITE.logo, loginLogo: SITE.loginLogo, logoSmall: SITE.logoSmall, name: SITE.name };
const listeners = new Set();
const subscribe = (cb) => { listeners.add(cb); return () => listeners.delete(cb); };

/** { logo, loginLogo, logoSmall, name } — re-renders when the owner's theme arrives. */
export const useSiteBrand = () => useSyncExternalStore(subscribe, () => brand);

function paint({ colors = {}, name, logo, favicon }) {
  const root = document.documentElement;
  Object.entries({ ...SITE.colors, ...colors }).forEach(([k, v]) => root.style.setProperty(k, v));
  let tag = document.getElementById('cf-theme');
  const custom = Object.keys(colors).length > 0;
  if (custom && !tag) {
    tag = document.createElement('style');
    tag.id = 'cf-theme';
    tag.textContent = OVERRIDES;
    document.head.appendChild(tag);
  } else if (!custom && tag) {
    tag.remove();
  }
  // One uploaded logo serves the header, login and change-password screens;
  // the uploaded favicon doubles as the collapsed sidebar's icon.
  brand = {
    logo: logo || SITE.logo,
    loginLogo: logo || SITE.loginLogo,
    logoSmall: favicon || SITE.logoSmall,
    name: name || SITE.name,
  };
  document.title = brand.name;
  const icon = document.querySelector('link[rel="icon"]');
  if (icon) icon.href = favicon || SITE.favicon;
  listeners.forEach((cb) => cb());
}

const readCache = () => {
  try { return JSON.parse(localStorage.getItem(CACHE_KEY)) || {}; } catch { return {}; }
};

/** Fetch the owner's theme (public betting-config) and apply + remember it. */
export async function loadSiteTheme() {
  try {
    const { data } = await api.get('/user/betting-config', { skipLoader: true });
    const t = data?.theme || {};
    const abs = (path) => (path ? `${API_BASE_URL}${path}` : '');
    const look = { colors: t.colors || {}, name: t.name || '', logo: abs(t.logo), favicon: abs(t.favicon) };
    paint(look);
    try { localStorage.setItem(CACHE_KEY, JSON.stringify(look)); } catch { /* private mode */ }
  } catch {
    // API down: keep the cached / built-in look.
  }
}

if (typeof document !== 'undefined') paint(readCache());
